import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { errorMessage, get, post, type DiffEntry, type Migration, type Run } from "../../lib/api";
import { duration, formatDate, formatNumber, KIND_LABELS, RUN_KIND_LABELS } from "../../lib/format";
import { Alert, Badge, Button, Card, ErrorBox, Loading, Modal, PageHeader, Select, Stat, StatusBadge, Table, Td } from "../../components/ui";
import { usePricing } from "./BillingPage";
import { MappingEditor } from "./MappingEditor";
import { can, useCatalog, useConnections, useWorkspace } from "./hooks";

const ACTIVE = new Set(["queued", "running"]);

export function MigrationDetailPage() {
  const { workspaceId: ws, migrationId } = useParams({ strict: false }) as { workspaceId: string; migrationId: string };
  const qc = useQueryClient();
  const detail = useWorkspace(ws);
  const catalog = useCatalog();
  const conns = useConnections(ws);
  const data = useQuery({
    queryKey: ["migration", ws, migrationId],
    queryFn: () => get<{ migration: Migration; runs: Run[] }>(`/api/workspaces/${ws}/migrations/${migrationId}`),
    refetchInterval: (q) => (q.state.data?.runs.some((r) => ACTIVE.has(r.status)) ? 1500 : false),
  });
  const [selected, setSelected] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: "ok" | "danger" | "warn"; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const runs = data.data?.runs ?? [];
  useEffect(() => {
    if (!selected && runs[0]) setSelected(runs[0].id);
  }, [runs, selected]);

  if (data.isLoading || detail.isLoading) return <Loading />;
  if (data.error) return <ErrorBox error={data.error} />;
  const m = data.data!.migration;
  const kit = catalog.data?.kits.find((k) => k.id === m.kitId);
  const src = conns.data?.find((c) => c.id === m.sourceConnectionId);
  const tgt = conns.data?.find((c) => c.id === m.targetConnectionId);
  const canRun = can(detail.data?.permissions, "migrations.run");
  const active = runs.find((r) => ACTIVE.has(r.status));
  const run = runs.find((r) => r.id === selected) ?? runs[0];
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["migration", ws, migrationId] });
    qc.invalidateQueries({ queryKey: ["ws", ws] });
  };

  const start = async (preview: boolean) => {
    setBusy(preview ? "preview" : "full");
    setMsg(null);
    try {
      const r = await post<{ run: Run; coveredByPack: string | null; reserved: number }>(`/api/workspaces/${ws}/migrations/${migrationId}/dry-run`, { preview });
      setSelected(r.run.id);
      if (r.coveredByPack) setMsg({ tone: "ok", text: "This migration is covered by a migration pack." });
      refresh();
    } catch (e) {
      setMsg({ tone: "danger", text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title={m.name}
        subtitle={
          <>
            {kit?.name ?? m.kitId} · {src ? `${KIND_LABELS[src.kind]}: ${src.name}` : "source"} → {tgt ? `${KIND_LABELS[tgt.kind]}: ${tgt.name}` : "target"} · <StatusBadge status={m.status} />
          </>
        }
        actions={
          canRun && (
            <>
              <Button variant="secondary" onClick={() => start(true)} disabled={!!active} loading={busy === "preview"}>
                Preview (free)
              </Button>
              <Button onClick={() => start(false)} disabled={!!active} loading={busy === "full"}>
                Full dry run
              </Button>
            </>
          )
        }
      />
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      {!runs.length && (
        <Alert tone="accent" title="How this works">
          1. Check the mapping below. 2. Preview the first 1,000 records for free. 3. Run a full dry run to see every change. 4. Run it. Every change is snapshotted, so you can revert.
        </Alert>
      )}

      {runs.length > 0 && (
        <Card
          className="mb-6"
          title="Runs"
          actions={
            <Select aria-label="Select run" value={run?.id} onChange={(e) => setSelected(e.target.value)} className="w-72">
              {runs.map((r) => (
                <option key={r.id} value={r.id}>
                  {RUN_KIND_LABELS[r.kind]}
                  {r.preview ? " (preview)" : ""} · {r.status} · {formatDate(r.createdAt)}
                </option>
              ))}
            </Select>
          }
        >
          {run && <RunPanel ws={ws} migration={m} run={run} latestDryRun={runs.find((r) => r.kind === "dryrun" && !r.preview && r.status === "completed")} canRun={canRun} onChange={(id) => { if (id) setSelected(id); refresh(); }} setMsg={setMsg} />}
        </Card>
      )}

      <MappingEditor ws={ws} migration={m} editable={canRun && !active} onSaved={refresh} />
    </>
  );
}

function RunPanel({
  ws,
  migration,
  run,
  latestDryRun,
  canRun,
  onChange,
  setMsg,
}: {
  ws: string;
  migration: Migration;
  run: Run;
  latestDryRun?: Run;
  canRun: boolean;
  onChange: (newRunId?: string) => void;
  setMsg: (m: { tone: "ok" | "danger" | "warn"; text: string } | null) => void;
}) {
  const [confirmApply, setConfirmApply] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const meta = useQuery({ queryKey: ["run", ws, run.id, run.status], queryFn: () => get<{ run: Run; hasReport: boolean; revertable: boolean }>(`/api/workspaces/${ws}/runs/${run.id}`) });
  const workspace = useWorkspace(ws);
  const pricing = usePricing();
  const diff = run.summary?.diff;
  const counts = run.summary?.counts as Record<string, number> | undefined;
  const isActive = ACTIVE.has(run.status);
  const progress = run.progress as { pages?: number; rowsRead?: number; chunk?: number; of?: number } | null;
  const mappingChanged = run.kind === "dryrun" && migration.updatedAt > (run.finishedAt ?? 0);

  const act = async (key: string, fn: () => Promise<{ run?: Run } | unknown>) => {
    setBusy(key);
    setMsg(null);
    try {
      const r = (await fn()) as { run?: Run };
      onChange(r?.run?.id);
    } catch (e) {
      setMsg({ tone: "danger", text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  };

  const writes = (diff?.create ?? 0) + (diff?.update ?? 0);
  const rate = migration.entity === "order" ? pricing.data?.rates.order_export_per_1k : pricing.data?.rates.export_per_1k;
  const estCredits = rate !== undefined ? Math.ceil((writes * rate) / 1000) : null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        <StatusBadge status={run.status} />
        <span className="muted">
          {RUN_KIND_LABELS[run.kind]}
          {run.preview ? " (preview, first 1,000 records)" : ""} · started {formatDate(run.startedAt)} · {duration(run.startedAt, run.finishedAt)}
        </span>
        <span className="muted">Credits used: {formatNumber(run.creditsUsed)}</span>
        {isActive && canRun && (
          <Button size="sm" variant="ghost" loading={busy === "cancel"} onClick={() => act("cancel", () => post(`/api/workspaces/${ws}/runs/${run.id}/cancel`))}>
            Cancel
          </Button>
        )}
      </div>

      {isActive && (
        <div className="mb-4">
          <div className="bg-surface-2 h-2 overflow-hidden rounded-full">
            <div className="bg-accent h-full transition-all" style={{ width: progress?.of ? `${Math.round(((progress.chunk ?? 0) / progress.of) * 100)}%` : "35%" }} />
          </div>
          <p className="muted mt-1 text-xs">
            {progress?.rowsRead !== undefined ? `${formatNumber(progress.rowsRead)} records read` : progress?.of ? `Batch ${progress.chunk} of ${progress.of}` : "Starting…"}
          </p>
        </div>
      )}
      {run.error && <Alert tone={run.status === "failed" ? "danger" : "warn"}>{run.error}</Alert>}

      {run.kind === "dryrun" && diff && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            <Stat label="Create" value={formatNumber(diff.create)} />
            <Stat label="Update" value={formatNumber(diff.update)} />
            <Stat label="Unchanged" value={formatNumber(diff.unchanged)} />
            <Stat label="Skipped" value={formatNumber(diff.skip)} />
            <Stat label="Consent protected" value={formatNumber(diff.consentProtected)} hint="Never re-subscribed" />
          </div>
          {run.summary?.truncated && <Alert tone="accent">Preview covers the first 1,000 records. Run a full dry run before running the migration.</Alert>}
          {diff.notRevertible > 0 && (
            <Alert tone="warn" title="Some changes can't be reverted">
              {formatNumber(diff.notRevertible)} writes can't be undone in the target. {run.summary?.reversibility?.note}
            </Alert>
          )}
          {diff.partiallyRevertible > 0 && <Alert tone="accent">{formatNumber(diff.partiallyRevertible)} changes can only be partly reverted. {run.summary?.reversibility?.note}</Alert>}
          {mappingChanged && <Alert tone="warn">The mapping changed after this dry run. Run a new dry run before running the migration.</Alert>}
          <div className="mb-4 flex flex-wrap gap-2">
            {canRun && !run.preview && run.status === "completed" && run.id === latestDryRun?.id && !mappingChanged && writes > 0 && (
              <Button onClick={() => setConfirmApply(true)}>Run migration ({formatNumber(writes)} writes)</Button>
            )}
            <a className="text-accent self-center text-sm underline" href={`/api/workspaces/${ws}/runs/${run.id}/diff.csv`}>
              Download full diff (CSV)
            </a>
          </div>
          <DiffViewer ws={ws} runId={run.id} />
        </>
      )}

      {run.kind === "apply" && counts && (
        <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
          <Stat label="Created" value={formatNumber(counts.created)} />
          <Stat label="Updated" value={formatNumber(counts.updated)} />
          <Stat label="Unchanged" value={formatNumber(counts.unchanged)} />
          <Stat label="Failed" value={formatNumber(counts.failed)} />
          <Stat label="Consent protected" value={formatNumber(counts.consentProtected)} />
        </div>
      )}
      {run.kind === "revert" && run.summary?.outcomes && (
        <div className="mb-4 flex flex-wrap gap-2">
          {Object.entries(run.summary.outcomes).map(([k, v]) => (
            <Badge key={k}>
              {k.replace(/_/g, " ")}: {formatNumber(v)}
            </Badge>
          ))}
        </div>
      )}

      {(run.kind === "apply" || run.kind === "revert") && !isActive && (
        <div className="flex flex-wrap gap-2">
          {meta.data?.hasReport && (
            <a href={`/api/workspaces/${ws}/runs/${run.id}/report`} target="_blank" rel="noreferrer">
              <Button variant="secondary">Open client report</Button>
            </a>
          )}
          {meta.data?.hasReport && canRun && (
            <Button
              variant="secondary"
              loading={busy === "share"}
              onClick={async () => {
                setBusy("share");
                try {
                  const r = await post<{ url: string }>(`/api/workspaces/${ws}/runs/${run.id}/report/share`, { days: 30 });
                  setShareUrl(r.url);
                  await navigator.clipboard?.writeText(r.url).catch(() => undefined);
                } catch (e) {
                  setMsg({ tone: "danger", text: errorMessage(e) });
                } finally {
                  setBusy(null);
                }
              }}
            >
              Share report link
            </Button>
          )}
          <a className="text-accent self-center text-sm underline" href={`/api/workspaces/${ws}/runs/${run.id}/errors.csv`}>
            Errors (CSV)
          </a>
          {run.kind === "apply" && canRun && meta.data?.revertable && (
            <Button variant="danger" onClick={() => setConfirmRevert(true)}>
              Revert this run
            </Button>
          )}
        </div>
      )}
      {shareUrl && (
        <Alert tone="ok" title="Share link created (copied), valid for 30 days">
          <code className="break-all">{shareUrl}</code>
        </Alert>
      )}

      <Modal
        open={confirmApply}
        onClose={() => setConfirmApply(false)}
        title="Run this migration?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmApply(false)}>
              Cancel
            </Button>
            <Button
              loading={busy === "apply"}
              onClick={() =>
                act("apply", async () => {
                  const r = await post<{ run: Run }>(`/api/workspaces/${ws}/migrations/${migration.id}/apply`, { dryRunId: run.id });
                  setConfirmApply(false);
                  return r;
                })
              }
            >
              Run migration
            </Button>
          </>
        }
      >
        <ul className="mb-4 list-disc space-y-1 pl-5 text-sm">
          <li>
            Writes <b>{formatNumber(diff?.create)}</b> new and <b>{formatNumber(diff?.update)}</b> updated records. Each record is re-checked against the target right before writing.
          </li>
          <li>Unsubscribed and suppressed contacts are never re-subscribed.</li>
          <li>Every overwritten value is snapshotted first, so the run can be reverted{workspace.data?.workspace.retention === "transit" ? " only while it's running (Transit retention)" : ""}.</li>
          {estCredits !== null && <li>Estimated cost: about {formatNumber(estCredits)} credits (unless a migration pack covers it). Credits are reserved first, and anything unused is returned.</li>}
        </ul>
        {workspace.data?.workspace.retention === "transit" && <Alert tone="warn">This workspace keeps no data after a run (Transit), so this run can't be reverted once it finishes.</Alert>}
      </Modal>

      <Modal
        open={confirmRevert}
        onClose={() => setConfirmRevert(false)}
        title="Revert this run?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmRevert(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={busy === "revert"}
              onClick={() =>
                act("revert", async () => {
                  const r = await post<{ run: Run }>(`/api/workspaces/${ws}/runs/${run.id}/revert`);
                  setConfirmRevert(false);
                  return r;
                })
              }
            >
              Revert
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Restores every value this run overwrote and removes the records it created, where the target allows it. Anything that can't be reverted (for example Klaviyo events, or a customer who has since placed an order) is listed in the revert report. Reverts are free.
        </p>
      </Modal>
    </div>
  );
}

function DiffViewer({ ws, runId }: { ws: string; runId: string }) {
  const [action, setAction] = useState<string>("");
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const q = useQuery({
    queryKey: ["diff", ws, runId, action, offset],
    queryFn: () => get<{ entries: DiffEntry[]; more: boolean }>(`/api/workspaces/${ws}/runs/${runId}/diff?limit=${limit}&offset=${offset}${action ? `&action=${action}` : ""}`),
  });
  const tone = (a: string) => (a === "create" ? "ok" : a === "update" ? "accent" : a === "error" ? "danger" : a === "skip" ? "warn" : "neutral") as "ok" | "accent" | "danger" | "warn" | "neutral";
  const fmt = (v: unknown) => (v === null || v === undefined || v === "" ? <span className="muted">∅</span> : typeof v === "object" ? JSON.stringify(v) : String(v));
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select aria-label="Filter by action" value={action} onChange={(e) => { setAction(e.target.value); setOffset(0); }} className="w-48">
          <option value="">All actions</option>
          {["create", "update", "unchanged", "skip", "error"].map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </Select>
      </div>
      {q.isLoading ? (
        <Loading />
      ) : (
        <Table head={["Record", "Action", "Changes", "Notes"]}>
          {q.data!.entries.map((e, i) => (
            <tr key={`${e.key}-${i}`}>
              <Td className="font-mono text-xs">{e.key}</Td>
              <Td>
                <Badge tone={tone(e.action)}>{e.action}</Badge>
              </Td>
              <Td className="max-w-lg">
                {e.changes.slice(0, 8).map((c) => (
                  <div key={c.field} className="text-xs">
                    <span className="font-medium">{c.field}</span>: {fmt(c.before)} → {fmt(c.after)}
                  </div>
                ))}
                {e.changes.length > 8 && <div className="muted text-xs">+{e.changes.length - 8} more</div>}
              </Td>
              <Td className="text-xs">
                {e.reason && <div className="muted">{e.reason}</div>}
                {e.protectedConsent?.map((p) => (
                  <div key={p.channel} className="text-warn">
                    {p.channel} consent protected: {p.reason}
                  </div>
                ))}
                {(e.action === "create" || e.action === "update") && e.reversible !== "full" && <div className="text-warn">{e.reversible === "none" ? "not revertible" : "partly revertible"}</div>}
              </Td>
            </tr>
          ))}
        </Table>
      )}
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
          Previous
        </Button>
        <Button size="sm" variant="secondary" disabled={!q.data?.more} onClick={() => setOffset(offset + limit)}>
          Next
        </Button>
      </div>
    </div>
  );
}
